import { Box, Grid, Typography, Divider, makeStyles, Theme } from "@material-ui/core";
import { ItemDisplay } from "./itemDisplay";
import { observer } from "mobx-react";
import { capitalizeOnlyFirstLetter } from "@roc/feature-comp-tool";
import { formatCurrencyField, formatNumberField, valueOrDash, google, map, zillow, getStringOrDefault } from "@roc/feature-valuation-review-v2";
import { PriceField } from "./priceField";
import { useStore } from "@roc/client-portal-shared/hooks";
import { segmentAnalytics, SegmentTrackerEventCompTool } from '@roc/feature-utils';
import { ComparableProperty } from "../types";

interface SubjectPropertyInfoProps {
  subjectProperty: ComparableProperty;
}

const useStyles = makeStyles((theme: Theme) => ({
  priceField: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "8px",
    padding: "0.5rem",
    color: "#FFFFFF",
    width: "300px",
  },
  imgLink: {
    width: "2.5rem",
    height: "2.5rem",
    cursor: "pointer"
  },
}));

const SubjectPropertyInfo = observer(({ subjectProperty }: SubjectPropertyInfoProps) => {
  const data = subjectProperty?.data;
  const classes = useStyles();
  const { compToolStoreV2: compToolStore } = useStore();
  const { isInternal, internalUsername, userStore } = compToolStore;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5">
              {capitalizeOnlyFirstLetter(data.address)}
            </Typography>
            <Typography variant="h6" style={{ fontWeight: "normal" }}>
              {capitalizeOnlyFirstLetter(data.school_district)}
            </Typography>
          </Box>
          <Box display="flex" gridGap="1rem">
            <img src={google} alt="Google Logo" className={classes.imgLink} onClick={() => {
              segmentAnalytics.trackEvent({
                name: SegmentTrackerEventCompTool.COMP_TOOL_OPEN_GOOGLE,
                userName: isInternal ? internalUsername : userStore.userInformation?.username,
                subjectAddress: subjectProperty.data.address,
                propertyAddress: data.address
              });
              window.open(`http://www.google.com/search?q=${data.address}`)
            }} />
            <img src={map} alt="Google maps Logo" className={classes.imgLink} onClick={() => {
              segmentAnalytics.trackEvent({
                name: SegmentTrackerEventCompTool.COMP_TOOL_OPEN_GOOGLE,
                userName: isInternal ? internalUsername : userStore.userInformation?.username,
                subjectAddress: subjectProperty.data.address,
                propertyAddress: data.address
              });
              window.open(`https://www.google.com/maps/search/?q=${data.address}`);
            }} />
            <img src={zillow} alt="Zillow Logo" className={classes.imgLink} onClick={() => {
              segmentAnalytics.trackEvent({
                name: SegmentTrackerEventCompTool.COMP_TOOL_OPEN_ZILLOW,
                userName: isInternal ? internalUsername : userStore.userInformation?.username,
                subjectAddress: subjectProperty.data.address,
                propertyAddress: data.address
              });
              window.open(data.zillow)
            }} />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gridGap="1rem"
        >
          <Box className={classes.priceField}>
            <PriceField property={subjectProperty} />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexGrow={1}
            marginLeft="2rem"
            marginRight="2rem"
          >
            <ItemDisplay label="Rm" value={valueOrDash(data.rooms)} biggerFont />
            <ItemDisplay label="Bd" value={valueOrDash(data.bedrooms)} biggerFont />
            <ItemDisplay label="Ba" value={valueOrDash(data.full_bathrooms)} biggerFont />
            <ItemDisplay label="HBa" value={valueOrDash(data.half_bathrooms)} biggerFont />
            <ItemDisplay label="Parking" value={valueOrDash(data.garage_type)} biggerFont />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexGrow={1}
        >
          {compToolStore.rentalComps ?
            (<ItemDisplay label="Last Sale Price" value={formatCurrencyField(data.price)} />) :
            (<ItemDisplay label="Rent/Mo." value={formatCurrencyField(data.rent)} />)}
          <ItemDisplay label="Lot Size" value={formatNumberField(data.lot_size)} />
          <ItemDisplay label="Taxes/Yr." value={formatCurrencyField(data.taxes)} />
          <ItemDisplay label="HOA" value={formatCurrencyField(data.hoa_fees)} />
          <ItemDisplay label="Yr. Built" value={valueOrDash(data.year_built)} />
          {compToolStore.rentalComps ?
            (<ItemDisplay label="Rent/Sq.Ft" value={formatCurrencyField(data.sqft_rent)} />) :
            (<ItemDisplay label="Price/Sq.Ft" value={formatCurrencyField(data.sqft_price)} />)
          }
          <ItemDisplay label="Sq.Ft" value={formatNumberField(data.area)} />
          <ItemDisplay label="Sale Type" value={valueOrDash(capitalizeOnlyFirstLetter(data.sale_type))} />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexGrow={1}
        >
          <ItemDisplay label="Condition" value={valueOrDash(capitalizeOnlyFirstLetter(data.condition_tag))} />
          <ItemDisplay label="Property Type" showTooltip={true} value={valueOrDash(capitalizeOnlyFirstLetter(data.property_type))} />
          <ItemDisplay label="Subject Zoning" value={valueOrDash(capitalizeOnlyFirstLetter(data.zoning))} />
          <ItemDisplay label="Property Status" value={data.is_active ? "Listed on MLS" : "Off Market"} />
        </Box>
      </Grid>
    </Grid>
  );
});

export default SubjectPropertyInfo;
